/**
 * #1034 — Polymarket US for the side rail, on the same bounded cadence as the
 * rail's US fills (#1007, usePolymarketUsFills): one read every 10 s, none
 * while the page is hidden, one as soon as it is visible again. The reads are
 * G1's saved records (relay database only, no Polymarket request).
 *
 * State is stamped with the identity it was read under (user, org and login
 * epoch) and compared again when the reply arrives, so a reply sent before a
 * logout or org switch never lands on the next session. A failed read keeps
 * the current identity's rows and says it failed, with when those rows were
 * read; it is never an empty list. So does one account whose read failed
 * (`carryUsRailFailedAccounts`): its last rows stay, marked as not current.
 *
 * Traders without a US account read nothing: the shared account-presence read
 * (#987) answers that once per login.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import { useUsAccountPresence } from '@/hooks/useUsAccountPresence';
import { recordUsVenueRead } from '@/hooks/useUsVenueStatus';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { carryUsRailFailedAccounts, readUsRail, readUsRailBalances, type UsRailBalance, type UsRailRead } from './usRailModel';

export const US_RAIL_POLL_MS = 10_000;

/** What a read says, for "has anything changed": every holding carries the
 * time its ledger snapshot was read, which is new on every read and shown
 * nowhere in the rail, so it is left out of the comparison. */
const readKey = (read: UsRailRead | null) =>
  JSON.stringify(read, (key, value: unknown) => (key === 'ledgerReadAtMs' ? undefined : value));

/**
 * #1034 — each account's balance as this login last saw it, so a re-read or a
 * failed refresh never blanks a figure already shown (K and P keep their last
 * figure too). Only within one identity: the caller passes the previous read
 * only when it was read under the same login.
 */
export function carryUsRailBalances(next: UsRailRead, prev: UsRailRead | null): UsRailRead {
  if (!prev) return next;
  const before = new Map(prev.accounts.map((a) => [a.accountId, a.balance]));
  return { accounts: next.accounts.map((a) => {
    const p = before.get(a.accountId);
    if (!p) return a;
    const last = p.state === 'saved' ? p.balance : p.state === 'unavailable' ? p.last : undefined;
    let balance: UsRailBalance = a.balance;
    if (a.balance.state === 'pending' && p.state !== 'pending' && p.state !== 'shared') balance = p;
    else if (a.balance.state === 'unavailable' && last) balance = { ...a.balance, last };
    return balance === a.balance ? a : { ...a, balance };
  }) };
}

type UsRailStatus = 'off' | 'loading' | 'ready' | 'unavailable';

type UsRailState = {
  status: UsRailStatus;
  /** The last read that succeeded for this identity, kept through a failed one. */
  read: UsRailRead | null;
  /** Code of the last failed read; null after a success. */
  error: string | null;
  /** When `read` was taken (ms since the epoch): the last read that answered,
   * including one that changed nothing. Null before the first. */
  readAtMs: number | null;
};

const IDLE: UsRailState = { status: 'loading', read: null, error: null, readAtMs: null };
const OFF: UsRailState = { status: 'off', read: null, error: null, readAtMs: null };

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const identityNow = () => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}:${getAuthSessionEpoch()}`;
};
const code = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

/** Whether this login has a US account it can view as its own (null = not
 * known yet), and a read on demand (after a cancel). */
export type UsRail = UsRailState & { hasUs: boolean | null; refresh: () => void };

export function useUsRail(enabled: boolean): UsRail {
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const who = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const authed = useAuthStore((s) => s.status === 'authenticated' && !!s.token && !!s.user?.orgId);
  // Nobody signed in, or a login with no organization: there is no US account
  // to speak of (not "unknown") — as the rail's US fills, which never read
  // without an org and keep their "No recent fills" (#1007).
  const noUsPossible = useAuthStore((s) => !s.user || !s.user.orgId);
  const identity = `${who}:${epoch}`;
  const presence = useUsAccountPresence(enabled && authed);
  // Known to have no US account: nothing to read and nothing to say.
  const live = enabled && authed && presence !== false;

  const [stamped, setStamped] = useState<UsRailState & { identity: string }>(() => ({ ...IDLE, identity }));
  const stateRef = useRef(stamped);
  const inFlight = useRef<string | null>(null);
  // A refresh asked for while a read is in flight (after a cancel or a sweep)
  // runs once more when that read settles, so its answer is never one taken
  // before the cancel landed.
  const again = useRef<string | null>(null);
  // #1034 — balances run in their own lane after each orders read, so a slow
  // balance never holds the orders lock (a post-cancel re-read, the 10 s poll
  // and the Cancel all count never wait for it). One at a time; the newest
  // orders read asked for meanwhile runs once when it settles.
  const balancesInFlight = useRef<string | null>(null);
  const balancesNext = useRef<{ identity: string; read: UsRailRead } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const readBalances = useCallback(async (orders: UsRailRead) => {
    if (balancesInFlight.current === identity) {
      balancesNext.current = { identity, read: orders };
      return;
    }
    balancesInFlight.current = identity;
    const current = () => mounted.current && identityNow() === identity;
    try {
      let next: UsRailRead | null = orders;
      while (next && current()) {
        balancesNext.current = null;
        let results: Map<string, UsRailBalance>;
        try {
          results = await readUsRailBalances(createUsBrowserPortfolioClient(), next, current);
        } catch {
          break; // a login change: nothing from this read is shown
        }
        const prev = stateRef.current;
        if (current() && prev.identity === identity && prev.read) {
          // Onto whatever the rail shows now (a newer orders read may have
          // landed meanwhile), by account; a failure keeps the shown figure.
          // Never onto an account the rail now shows as shared: its cash is
          // no longer this trader's to see (a desk grant removed meanwhile).
          const merged = carryUsRailBalances({ accounts: prev.read.accounts.map((a) => {
            const b = results.get(a.accountId);
            return b && a.balance.state !== 'shared' ? { ...a, balance: b } : a;
          }) }, prev.read);
          if (readKey(merged) !== readKey(prev.read)) {
            const resolved = { ...prev, read: merged, identity };
            stateRef.current = resolved;
            setStamped(resolved);
          }
        }
        // Set by a call made while this one was awaiting (TS cannot see that).
        const queued = balancesNext.current as { identity: string; read: UsRailRead } | null;
        next = queued?.identity === identity ? queued.read : null;
      }
    } finally {
      if (balancesInFlight.current === identity) balancesInFlight.current = null;
      if (balancesNext.current?.identity === identity) balancesNext.current = null;
    }
  }, [identity]);

  const refresh = useCallback(async () => {
    if (inFlight.current === identity) {
      again.current = identity;
      return;
    }
    inFlight.current = identity;
    const current = () => mounted.current && identityNow() === identity;
    const carry = (): UsRailState => (stateRef.current.identity === identity ? stateRef.current : IDLE);
    const apply = (next: UsRailState) => {
      if (!current()) return;
      const prev = stateRef.current;
      const resolved = { ...next, identity };
      // An unchanged answer is not a new state: the Positions page hosts this
      // read, so a 10 s poll that found nothing new must not re-render it.
      // Its read time is still kept, so a later failure says how fresh the
      // rows it keeps really are (nothing shows that time while reads answer).
      const unchanged = prev.identity === identity && prev.status === next.status && prev.error === next.error
        && readKey(prev.read) === readKey(next.read);
      stateRef.current = resolved;
      // The US dot in the chrome (R4-F1) follows this read rather than reading itself.
      recordUsVenueRead(identity, resolved);
      if (!unchanged) setStamped(resolved);
    };
    try {
      do {
        again.current = null;
        try {
          // Fresh clients per read: an abandoned read settling late resets only
          // its own client's selection (the #1006 Cancel all lesson).
          const read = await readUsRail(
            { accounts: createUsBrowserAccountClient(), portfolio: createUsBrowserPortfolioClient() }, current);
          const readAtMs = Date.now();
          const prev = stateRef.current.identity === identity ? stateRef.current : null;
          // A failed account keeps its last rows, marked with when they were read.
          const carried = carryUsRailFailedAccounts(read, prev?.read ?? null, prev?.readAtMs ?? null);
          // Shown at once, each balance still as last shown; then the balance lane.
          apply({ status: 'ready', read: carryUsRailBalances(carried, prev?.read ?? null), error: null, readAtMs });
          if (current()) void readBalances(read);
        } catch (error) {
          apply({ ...carry(), status: 'unavailable', error: code(error) });
        }
      } while (again.current === identity && current());
    } finally {
      if (inFlight.current === identity) inFlight.current = null;
      if (again.current === identity) again.current = null;
    }
  }, [identity, readBalances]);

  useEffect(() => {
    if (!live) return;
    let stopped = false;
    let lastPollMs = Number.NEGATIVE_INFINITY;
    const poll = () => {
      if (stopped || document.hidden) return;
      lastPollMs = performance.now();
      void refresh();
    };
    const onVisibility = () => {
      if (performance.now() - lastPollMs >= US_RAIL_POLL_MS) poll();
    };
    poll();
    const intervalId = window.setInterval(poll, US_RAIL_POLL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [live, refresh]);

  const readNow = useCallback(() => { if (live) void refresh(); }, [live, refresh]);
  if (!live) return { ...OFF, hasUs: presence === false || noUsPossible ? false : null, refresh: readNow };
  const state = stamped.identity === identity ? stamped : IDLE;
  // The rail's own read answers first; until it has, the shared presence read.
  return { ...state, hasUs: state.read ? state.read.accounts.length > 0 : presence, refresh: readNow };
}
